import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, TextField, Button, Switch, FormControlLabel, CircularProgress,
  Snackbar, Alert, Tabs, Tab
} from '@mui/material';
import axios from 'axios';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>
);

export default function LegalContent() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [termsAndConditions, setTermsAndConditions] = useState({ content: '', version: '1.0.0', effectiveDate: new Date().toISOString().split('T')[0], isRequired: true });
  const [privacyPolicy, setPrivacyPolicy] = useState({ content: '', version: '1.0.0', effectiveDate: new Date().toISOString().split('T')[0], isRequired: true });

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/config/bootstrap');
      if (response.data.success) {
        if (response.data.config.termsAndConditions) setTermsAndConditions(response.data.config.termsAndConditions);
        if (response.data.config.privacyPolicy) setPrivacyPolicy(response.data.config.privacyPolicy);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load legal documents', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTerms = async () => {
    try {
      setSaving(true);
      const response = await axios.post('/api/config/terms-and-conditions', { termsAndConditions, updatedBy: 'admin' });
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Terms and conditions updated', severity: 'success' });
        if (response.data.termsAndConditions) setTermsAndConditions(response.data.termsAndConditions);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save terms', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrivacy = async () => {
    try {
      setSaving(true);
      const response = await axios.post('/api/config/privacy-policy', { privacyPolicy, updatedBy: 'admin' });
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Privacy policy updated', severity: 'success' });
        if (response.data.privacyPolicy) setPrivacyPolicy(response.data.privacyPolicy);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save privacy policy', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Manage Terms & Conditions and Privacy Policy for the Novo Solution app.
      </Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Terms & Conditions" />
        <Tab label="Privacy Policy" />
      </Tabs>

      {/* Terms & Conditions */}
      <TabPanel value={tabValue} index={0}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Users must accept these terms during app initialization. Updating terms may require re-acceptance.
        </Alert>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={15} label="Terms and Conditions Content" value={termsAndConditions.content}
              onChange={(e) => setTermsAndConditions(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Enter your terms and conditions here. Supports Markdown." helperText="Supports Markdown formatting" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Version" value={termsAndConditions.version} InputProps={{ readOnly: true }} helperText="Auto-incremented on save" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label="Effective Date" value={termsAndConditions.effectiveDate}
              onChange={(e) => setTermsAndConditions(prev => ({ ...prev, effectiveDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={termsAndConditions.isRequired} onChange={(e) => setTermsAndConditions(prev => ({ ...prev, isRequired: e.target.checked }))} />}
              label="Require users to accept terms (recommended)" />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">Last modified: {termsAndConditions.lastModified ? new Date(termsAndConditions.lastModified).toLocaleDateString() : 'Never'}</Typography>
          <Button variant="contained" onClick={handleSaveTerms} disabled={saving || !termsAndConditions.content.trim()}>
            {saving ? <CircularProgress size={24} /> : 'Save Terms & Conditions'}
          </Button>
        </Box>
      </TabPanel>

      {/* Privacy Policy */}
      <TabPanel value={tabValue} index={1}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Describes how user data is collected, used, and protected.
        </Alert>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={15} label="Privacy Policy Content" value={privacyPolicy.content}
              onChange={(e) => setPrivacyPolicy(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Enter your privacy policy here. Supports Markdown." helperText="Supports Markdown formatting" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Version" value={privacyPolicy.version} InputProps={{ readOnly: true }} helperText="Auto-incremented on save" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label="Effective Date" value={privacyPolicy.effectiveDate}
              onChange={(e) => setPrivacyPolicy(prev => ({ ...prev, effectiveDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={privacyPolicy.isRequired} onChange={(e) => setPrivacyPolicy(prev => ({ ...prev, isRequired: e.target.checked }))} />}
              label="Require users to review privacy policy (recommended)" />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">Last modified: {privacyPolicy.lastModified ? new Date(privacyPolicy.lastModified).toLocaleDateString() : 'Never'}</Typography>
          <Button variant="contained" onClick={handleSavePrivacy} disabled={saving || !privacyPolicy.content.trim()}>
            {saving ? <CircularProgress size={24} /> : 'Save Privacy Policy'}
          </Button>
        </Box>
      </TabPanel>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}><Alert severity={snackbar.severity}>{snackbar.message}</Alert></Snackbar>
    </Box>
  );
}

